import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Clock, Calendar, Star, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Survey {
  id: string;
  title: string;
  description: string;
  survey_type: string;
  course_id?: string;
  course_title?: string;
  created_at: string;
  questions: any[];
  is_completed?: boolean;
  completion_date?: string;
  response_id?: string;
}

interface UserSurveyDashboardProps {
  onTakeSurvey: (survey: Survey) => void;
}

export function UserSurveyDashboard({ onTakeSurvey }: UserSurveyDashboardProps) {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [completedSurveys, setCompletedSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadSurveys();
  }, []);

  const loadSurveys = async () => {
    try {
      // Mock data for now - in real implementation would fetch from supabase
      const mockSurveys: Survey[] = [
        {
          id: '1',
          title: 'Professional Development Course Feedback',
          description: 'Please evaluate the effectiveness of your recent PD course',
          survey_type: 'course_evaluation',
          course_id: 'course-1',
          course_title: 'Classroom Management Strategies',
          created_at: '2024-01-15T00:00:00Z',
          questions: [
            {
              id: 'overall_rating',
              type: 'rating',
              question: 'Overall, how would you rate this course?',
              required: true,
              options: { scale: 5 }
            }
          ],
          is_completed: false
        },
        {
          id: '2',
          title: 'Mid-Year Performance Check-in',
          description: 'Reflect on your professional growth and development',
          survey_type: 'performance_evaluation',
          created_at: '2024-01-10T00:00:00Z',
          questions: [],
          is_completed: false
        },
        {
          id: '3',
          title: 'Technology Integration Workshop Evaluation',
          description: 'Share your feedback on the recent tech workshop',
          survey_type: 'course_evaluation',
          course_id: 'course-2',
          course_title: 'Digital Tools for Education',
          created_at: '2023-12-20T00:00:00Z',
          questions: [],
          is_completed: true,
          completion_date: '2023-12-22T00:00:00Z',
          response_id: 'resp-1'
        }
      ];

      const pending = mockSurveys.filter(s => !s.is_completed);
      const completed = mockSurveys.filter(s => s.is_completed);
      
      setSurveys(pending);
      setCompletedSurveys(completed);
    } catch (error) {
      console.error('Error loading surveys:', error);
      toast({
        title: "Error loading surveys",
        description: "Failed to load your surveys",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getSurveyTypeIcon = (type: string) => {
    switch (type) {
      case 'course_evaluation':
        return <Star className="h-4 w-4" />;
      case 'performance_evaluation':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getSurveyTypeBadge = (type: string) => {
    switch (type) {
      case 'course_evaluation':
        return <Badge variant="secondary">Course Feedback</Badge>;
      case 'performance_evaluation':
        return <Badge variant="outline">Performance Review</Badge>;
      default:
        return <Badge variant="default">Survey</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading your surveys...</div>;
  }

  const completionRate = surveys.length > 0 
    ? Math.round((completedSurveys.length / (surveys.length + completedSurveys.length)) * 100)
    : 100;

  return (
    <div className="space-y-6">
      {/* Survey Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Surveys</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{surveys.length}</div>
            <p className="text-xs text-muted-foreground">
              {surveys.length === 0 ? 'All caught up!' : 'Waiting for your response'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedSurveys.length}</div>
            <p className="text-xs text-muted-foreground">Surveys completed</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completionRate}%</div>
            <Progress value={completionRate} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Pending Surveys */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Surveys to Complete</h2>
        {surveys.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium">All caught up!</h3>
              <p className="text-muted-foreground">You have no pending surveys at this time.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {surveys.map((survey) => (
              <Card key={survey.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <CardTitle className="flex items-center gap-2">
                        {getSurveyTypeIcon(survey.survey_type)}
                        {survey.title}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">{survey.description}</p>
                      {survey.course_title && (
                        <p className="text-sm font-medium text-primary">
                          Related to: {survey.course_title}
                        </p>
                      )}
                    </div>
                    {getSurveyTypeBadge(survey.survey_type)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4 inline mr-1" />
                      Assigned {formatDate(survey.created_at)}
                    </div>
                    <Button onClick={() => onTakeSurvey(survey)}>
                      Take Survey
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Completed Surveys */}
      {completedSurveys.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Completed Surveys</h2>
          <div className="grid gap-4">
            {completedSurveys.map((survey) => (
              <Card key={survey.id} className="bg-muted/50">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        {survey.title}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">{survey.description}</p>
                      {survey.course_title && (
                        <p className="text-sm font-medium text-primary">
                          Related to: {survey.course_title}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {getSurveyTypeBadge(survey.survey_type)}
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        Completed
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    Completed {survey.completion_date ? formatDate(survey.completion_date) : 'Recently'}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}